// @owners { team: patient-health }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import { useScreenSize } from '@alto/design-system';
import { YoutubePlayer } from '@alto/features';
// eslint-disable-next-line @alto/no-pocky-import
import { Button, Column, H4, Row, spacing } from '@alto/pocky';
import React from 'react';
import styled from 'styled-components';
import { type Video } from '~shared/features/instructional-videos/types';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import DescriptionPane from '~web/features/instructional-videos/components/DescriptionPane';

const PaddedH4 = styled(H4)`
  padding: ${SPACING.STATIC.MD.rem} ${SPACING.STATIC.MD.rem} 0;
`;

const VideoWrapper = styled(Column)<{ includeShadow: boolean }>`
  background: ${COLORS.BACKGROUND_COLORS.WHITE};
  padding: ${SPACING.STATIC.XL.rem} ${SPACING.STATIC.MD.rem};
  box-shadow: ${({ includeShadow }) =>
    includeShadow
      ? '0 0 0 1px rgba(147, 156, 163, 0.1), 0 3px 5px rgba(195, 207, 217, 0.3), 0 5px 15px rgba(195, 207, 217, 0.3)'
      : 'unset'};
`;

const LargeScreenVideoWrapper = styled(VideoWrapper)<{ includeShadow: boolean; isLGScreenOrBigger: boolean }>`
  ${({ isLGScreenOrBigger }) => !isLGScreenOrBigger && `display: none;`}
`;

const SmallScreenVideoWrapper = styled(VideoWrapper)<{ includeShadow: boolean; isLGScreenOrBigger: boolean }>`
  display: none;
  ${({ isLGScreenOrBigger }) => !isLGScreenOrBigger && `display: unset;`}
`;

const VideoAndDetailsWrapper = styled(Row)`
  align-items: flex-start;
`;

const DescriptionWrapper = styled.div<{ isLGScreenOrBigger: boolean }>`
  max-width: 50%;
  ${({ isLGScreenOrBigger }) => !isLGScreenOrBigger && `max-width: unset;`}
`;

export type Props = {
  readonly videos: Video[];
  readonly openVideoPrepInfoModal: (video: Video) => void;
  readonly navigateToVideosPage: () => void;
};

const MedListInstructionalVideos = ({ videos, openVideoPrepInfoModal, navigateToVideosPage }: Props) => {
  const { isLGScreenOrBigger } = useScreenSize();
  return (
    <Column>
      <PaddedH4>Fertility Instructional Videos</PaddedH4>
      {videos.map((video, index) => {
        const allVideosButton = index === videos.length - 1 && (
          <Button
            full
            kind="secondary"
            onClick={navigateToVideosPage}
          >
            All Instructional Videos
          </Button>
        );
        return (
          <>
            <SmallScreenVideoWrapper
              key={video.videoID}
              spacing={spacing.smSpacing}
              includeShadow={index > 0}
              isLGScreenOrBigger={isLGScreenOrBigger}
            >
              <YoutubePlayer
                title={video.title}
                videoID={video.videoID}
                origin={ORIGIN_NAMES.MED_LIST}
              />
              <Button
                full
                kind="secondary"
                onClick={() => {
                  openVideoPrepInfoModal(video);
                }}
              >
                Preparation Info
              </Button>
              {allVideosButton}
            </SmallScreenVideoWrapper>
            <LargeScreenVideoWrapper
              key={video.videoID}
              spacing={spacing.mdSpacing}
              includeShadow={index > 0}
              isLGScreenOrBigger={isLGScreenOrBigger}
            >
              <VideoAndDetailsWrapper spacing={spacing.mdSpacing}>
                <YoutubePlayer
                  videoID={video.videoID}
                  origin={ORIGIN_NAMES.MED_LIST}
                />
                <DescriptionWrapper isLGScreenOrBigger={isLGScreenOrBigger}>
                  <DescriptionPane video={video} />
                </DescriptionWrapper>
              </VideoAndDetailsWrapper>
              {allVideosButton}
            </LargeScreenVideoWrapper>
          </>
        );
      })}
    </Column>
  );
};

export default MedListInstructionalVideos;
